import { useState } from "react";
import { Plus, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useResource, useGet } from "../lib/hooks";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { RecordDialog } from "./RecordDialog";
import { ResourceManager } from "./ResourceManager";

export function DivisionsTab() {
  return (
    <ResourceManager resource="divisions" label="Division" testid="divisions"
      fields={[{ key: "name", label: "Division name", required: true, full: true }, { key: "code", label: "Code (leave blank to create one from the name)", placeholder: "e.g. NDIS" }, { key: "description", label: "Description", type: "textarea" }]}
      columns={[{ key: "name", label: "Division" }, { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs text-[#C2A462]">CODE: {r.code || "—"}</span> }, { key: "description", label: "Description" }]} />
  );
}

function PositionsPanel() {
  const { items, save, remove } = useResource("positions");
  const [val, setVal] = useState("");
  const list = [...(items || [])].sort((a, b) => a.name.localeCompare(b.name));
  const add = async () => {
    const v = val.trim();
    if (!v) return;
    if (list.some((p) => p.name.toLowerCase() === v.toLowerCase())) return toast.error("Position already exists");
    await save({ name: v });
    setVal("");
  };
  return (
    <div className="rounded-md border bg-card p-5" data-testid="positions-panel">
      <div className="overline mb-1">Positions</div>
      <p className="mb-4 text-xs text-muted-foreground">Job titles. Each person has one position, chosen in Users & roles.</p>
      <div className="mb-4 flex flex-wrap gap-2">
        {list.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full border border-[#C2A462]/35 bg-[#C2A462]/10 px-3 py-1 text-[13px]" data-testid={`position-${p.name}`}>
            {p.name}<button onClick={() => window.confirm(`Remove position "${p.name}"? People keep it until you change their position.`) && remove(p.id)} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${p.name}`} data-testid={`remove-position-${p.id}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {!list.length && <span className="text-sm text-muted-foreground">No positions yet.</span>}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Add a position, e.g. Support Worker" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="add-position-input" />
        <Button variant="outline" onClick={add} data-testid="add-position-btn"><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function GroupCard({ g, users, groupIds, onRename, onDelete, reloadUsers, extraAccess }) {
  const [pick, setPick] = useState("");
  const members = users.filter((u) => (u.groups || []).includes(g.id));
  const setMember = async (u, on) => {
    const valid = new Set(groupIds);
    const next = (on ? [...new Set([...(u.groups || []), g.id])] : (u.groups || []).filter((x) => x !== g.id)).filter((x) => valid.has(x));
    try { await api.put(`/users/${u.id}`, { name: u.name, email: u.email, role: u.role, groups: next }); reloadUsers(); setPick(""); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-md border bg-card p-5" data-testid={`staff-group-${g.id}`}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="font-semibold text-[#EBD6A0]">{g.name}{extraAccess && <span className="ml-2 rounded-full border border-[#C2A462]/40 px-2 py-0.5 text-[10px] font-normal text-[#C2A462]" data-testid={`group-access-${g.id}`}>+ {extraAccess}</span>}</div>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" onClick={onRename} aria-label="Rename group" data-testid={`rename-group-${g.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={onDelete} aria-label="Delete group" data-testid={`delete-group-${g.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
        </div>
      </div>
      {g.description && <p className="mb-2 text-xs text-muted-foreground">{g.description}</p>}
      <div className="mb-3 text-[11px] uppercase tracking-[0.14em] text-slate-500">{members.length} {members.length === 1 ? "member" : "members"}</div>
      <div className="mb-3 space-y-1">
        {members.map((u) => <div key={u.id} className="flex items-center justify-between rounded border border-white/5 px-2.5 py-1.5 text-[13px]" data-testid={`group-member-${g.id}-${u.id}`}>
          <span>{u.name}<span className="ml-1.5 text-xs text-muted-foreground">{u.position}</span></span>
          <button onClick={() => setMember(u, false)} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${u.name}`} data-testid={`remove-member-${g.id}-${u.id}`}><X className="h-3.5 w-3.5" /></button></div>)}
      </div>
      <select value={pick} onChange={(e) => { const u = users.find((x) => x.id === e.target.value); if (u) setMember(u, true); }} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`add-member-${g.id}`}>
        <option value="">+ Add a person…</option>
        {users.filter((u) => !members.includes(u) && !u.disabled).map((u) => <option key={u.id} value={u.id}>{u.name}{u.position ? ` — ${u.position}` : ""}</option>)}
      </select>
    </div>
  );
}

export function PositionsTab() {
  const { items, save, remove } = useResource("staff_groups");
  const [users, reloadUsers] = useGet("/users?include_owner=1");
  const [profiles] = useGet("/access-profiles");
  const [edit, setEdit] = useState(null);
  const profName = (id) => (profiles || []).find((p) => p.id === id)?.name;
  return (
    <div className="space-y-6">
      <PositionsPanel />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><div className="overline">Groups</div><p className="text-xs text-muted-foreground">Teams and committees such as Management or Q&S Committee. A person can be in several groups. Give a group extra access and every member gets it on top of their own access level, and loses it when removed.</p></div>
        <Button onClick={() => setEdit({})} data-testid="add-staff-group-btn"><Plus className="h-4 w-4 mr-1" />New group</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[...(items || [])].sort((a, b) => a.name.localeCompare(b.name)).map((g) => <GroupCard key={g.id} g={g} extraAccess={profName(g.access_profile_id)} users={users || []} groupIds={(items || []).map((x) => x.id)} reloadUsers={reloadUsers} onRename={() => setEdit(g)}
          onDelete={() => window.confirm(`Delete group "${g.name}"? Members are removed from it.`) && remove(g.id)} />)}
        {!items?.length && <p className="text-sm text-muted-foreground">No groups yet.</p>}
      </div>
      {edit && <RecordDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Edit group" : "New group"} initial={edit}
        fields={[{ key: "name", label: "Group name (e.g. Management)", required: true, full: true }, { key: "description", label: "Description", type: "textarea" },
          { key: "access_profile_id", label: "Extra access for members", type: "select", placeholder: "No extra access", hint: "Added on top of each member's own access level (staff only).", options: [{ value: "", label: "No extra access" }, ...(profiles || []).map((p) => ({ value: p.id, label: p.name }))] }]}
        onSave={async (f) => { await save({ name: f.name, description: f.description || "", access_profile_id: f.access_profile_id || null }, edit.id); toast.success("Group saved"); }} />}
    </div>
  );
}
